<template><div><code-well-header class="d-p0">
    <dt-banner title="Example banner" kind="info" class="d-ps-relative d-zi-base">
        Message body with a <dt-link kind="muted">Link</dt-link>
    </dt-banner>
</code-well-header>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">System banners are used for <strong>system</strong> messaging. They are full-width notices placed in one of two locations:</p>
<ol class="d-docsite--ordered-list">
<li class="d-docsite--list-element"><strong>Above everything else:</strong> If the banner is related to the entire app (e.g. the user lost internet service), place the banner first. <em>These should not be dismissable until the issue is resolved.</em> To pin the banner to the top of the app, add the <code v-pre>.d-notice--pinned</code> class.</li>
<li class="d-docsite--list-element"><strong>Below the top bar:</strong> This is the default location for system banners. Use these when it affects only a particular area of the product (e.g. when the user is in Do Not Disturb mode).</li>
</ol>
<p class="d-docsite--paragraph">Banners are a type of notice and so you can use the following <RouteLink to="/components/notice.html" class="d-docsite--link d-link">Notice</RouteLink> styles in conjunction with <code v-pre>.d-banner</code>.</p>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="kind" tabindex="-1" class="d-docsite--header-3">Kind <a class="header-anchor d-link d-docsite--link d-link" href="#kind" aria-hidden="true">#</a></h3>
<code-well-header>
    <div class="d-d-flex d-w100p d-flow8 d-ai-flex-end">
        <div class="d-fl-grow1">
            <dt-select-menu label="Style" :options="bannerOptions" @change="changeKind" />
        </div>
        <dt-checkbox value="important" @input="toggleImportant">Important</dt-checkbox>
        <dt-button @click="toggleBanner('example-kind')">Toggle Example</dt-button>
    </div>
</code-well-header>
<dt-banner
  :pinned="pinned"
  :important="important"
  :kind="selectedKind"
  title="Optional banner title"
  v-show="shownBanner === 'example-kind'"
  @close="closeBanner"
>
  Message body
</dt-banner>
<code-example-tabs
htmlCode='
<aside class="d-banner d-banner--base" role="alert" aria-hidden="false">
  <div class="d-banner__dialog" role="alertdialog" aria-labelledy="info-alert-title" aria-describedby="info-alert-desc">
    <div class="d-notice__icon">...</div>
    <div class="d-notice__content">
      <h2 class="d-notice__title" id="info-alert-title">...</h2>
      <p class="d-notice__message" id="info-alert-desc">...</p>
    </div>
  <div class="d-notice__actions">...</div>
</aside>
<aside class="d-banner d-banner--error" role="alert" aria-hidden="false">...</aside>
<aside class="d-banner d-banner--info" role="alert" aria-hidden="false">...</aside>
<aside class="d-banner d-banner--success" role="alert" aria-hidden="false">...</aside>
<aside class="d-banner d-banner--warning" role="alert" aria-hidden="false">...</aside>
'
vueCode='
<dt-banner kind="base" title="Optional banner title"> Message body </dt-banner>
<dt-banner kind="error" title="Optional banner title"> Message body </dt-banner>
<dt-banner kind="info" title="Optional banner title"> Message body </dt-banner>
<dt-banner kind="success" title="Optional banner title"> Message body </dt-banner>
<dt-banner kind="warning" title="Optional banner title"> Message body </dt-banner>
<dt-banner background-image="{$background-image}" background-size="contain"> Message body </dt-banner>
<dt-banner pinned="true" kind="warning" title="Optional banner title"> Message body </dt-banner>
<dt-banner important="true" kind="warning" title="Optional banner title"> Message body </dt-banner>
'
/>
<h3 id="pinned" tabindex="-1" class="d-docsite--header-3">Pinned <a class="header-anchor d-link d-docsite--link d-link" href="#pinned" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Pins the banner to the top of the window.</p>
<code-well-header>
    <div class="d-d-flex d-w100p d-flow8 d-ai-flex-end">
        <dt-button @click="toggleBanner('example-pinned')">Toggle Example</dt-button>
    </div>
</code-well-header>
<dt-banner
  :pinned="true"
  title="Optional banner title"
  v-show="shownBanner === 'example-pinned'"
  @close="closeBanner"
>
  Message body
</dt-banner>
<code-example-tabs
htmlCode='
<aside class="d-banner d-banner--base d-banner--pinned" role="alert" aria-hidden="false">...</aside>
'
vueCode='
<dt-banner kind="base" title="Optional banner title" :pinned="true"> Message body </dt-banner>
'
/>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="banner" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="banner"></component-class-table><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<component-accessible-table component-name="banner"></component-accessible-table></div></template>


<script setup>
import { ref } from 'vue';
import { accessible } from '@data/banner.json';

const bannerOptions = [
  { value: 'base', label: 'Base' },
  { value: 'error', label: 'Error' },
  { value: 'info', label: 'Info' },
  { value: 'success', label: 'Success' },
  { value: 'warning', label: 'Warning' },
];

const shownBanner = ref(null);
const important = ref(false);
const pinned = ref(false);
const selectedKind = ref('base');

function toggleBanner (id) {
  if (shownBanner.value === id) {
    shownBanner.value = null;
  } else {
    shownBanner.value = id;
  }
}

function toggleImportant () {
  important.value = !important.value;
}

function changeKind (kind) {
  selectedKind.value = kind;
}

function closeBanner () {
  shownBanner.value = null;
}
</script>